import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Star, ArrowRight, Quote, Newspaper, Facebook, Instagram, Youtube } from "lucide-react";
import { api, mediaUrl, euros, formatDate } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import Reveal from "@/components/Reveal";

export default function Social() {
  const { content, settings } = useSite();
  const [pos, setPos] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/public/points-of-sale").then((r) => setPos(r.data.slice(0, 4))).catch(() => {});
    api.get("/public/reviews").then((r) => setReviews(r.data.slice(0, 3))).catch(() => {});
    api.get("/public/blog").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <>
      <section data-testid="pos-preview-section" className="py-16 sm:py-24 bg-white border-y border-line">
        <div className="container-x">
          <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
            <div>
              <p className="eyebrow mb-4">Points de vente</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase leading-[1.1] text-onyx">
                Où trouver le jeu ?
              </h2>
            </div>
            <Link to="/ou-trouver-le-jeu" data-testid="pos-preview-cta"
              className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-rouge hover:text-rouge-hover transition-colors">
              Tous les points de vente <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pos-preview-grid">
            {pos.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <div className="h-full bg-cream border border-line p-6">
                  <MapPin className="w-5 h-5 text-rouge mb-3" />
                  <h3 className="font-display font-bold text-onyx leading-snug">{p.name}</h3>
                  {p.address && <p className="text-sm text-muted-foreground mt-2">{p.address}</p>}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {reviews.length > 0 && (
        <section data-testid="reviews-section" className="py-16 sm:py-24">
          <div className="container-x">
            <Reveal className="mb-10 sm:mb-14">
              <p className="eyebrow mb-4">Ils ont adopté le jeu</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase leading-[1.1] text-onyx">
                La tribune en parle.
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="reviews-grid">
              {reviews.map((r, i) => (
                <Reveal key={r.id} delay={i * 0.08}>
                  <figure className={`h-full p-8 border ${r.featured ? "bg-onyx text-cream border-onyx" : "bg-white border-line"}`}>
                    <Quote className={`w-6 h-6 mb-4 ${r.featured ? "text-rouge-light" : "text-rouge"}`} />
                    <blockquote className={`text-sm sm:text-base leading-relaxed ${r.featured ? "text-cream/85" : "text-muted-foreground"}`}>
                      {r.text}
                    </blockquote>
                    <figcaption className="mt-6 flex items-center gap-3">
                      {r.photo && <img src={mediaUrl(r.photo)} alt={r.author} className="w-10 h-10 rounded-full object-cover" loading="lazy" />}
                      <div>
                        <p className={`font-display font-bold text-sm ${r.featured ? "text-cream" : "text-onyx"}`}>{r.author}</p>
                        {r.role && <p className={`text-xs ${r.featured ? "text-cream/55" : "text-muted-foreground"}`}>{r.role}</p>}
                      </div>
                      {r.rating && (
                        <span className="ml-auto flex gap-0.5" aria-label={`${r.rating} sur 5`}>
                          {Array.from({ length: r.rating }).map((_, s) => (
                            <Star key={s} className="w-3.5 h-3.5 fill-gold text-gold" />
                          ))}
                        </span>
                      )}
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
            <Reveal className="mt-8">
              <Link to="/avis" data-testid="reviews-cta" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-rouge hover:text-rouge-hover transition-colors">
                Tous les avis <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {posts.length > 0 && (
        <section data-testid="news-section" className="py-16 sm:py-24 bg-sand">
          <div className="container-x">
            <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
              <div>
                <p className="eyebrow mb-4">Actualités</p>
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase leading-[1.1] text-onyx">
                  Le vestiaire.
                </h2>
              </div>
              <Link to="/actualites" data-testid="news-cta"
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-rouge hover:text-rouge-hover transition-colors">
                Toutes les actualités <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="news-grid">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.08}>
                  <Link to={`/actualites/${p.slug}`} data-testid={`news-card-${p.slug}`} className="group block bg-white border border-line h-full hover:border-rouge/40 transition-colors">
                    {p.image && (
                      <div className="aspect-[16/10] overflow-hidden bg-white">
                        <img src={mediaUrl(p.image)} alt={p.title} loading="lazy"
                          className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-500" />
                      </div>
                    )}
                    <div className="p-6">
                      <p className="font-mono text-xs text-muted-foreground mb-2 flex items-center gap-2">
                        <Newspaper className="w-3.5 h-3.5 text-rouge" /> {formatDate(p.published_at)}
                      </p>
                      <h3 className="font-display font-bold text-lg leading-snug text-onyx group-hover:text-rouge transition-colors">{p.title}</h3>
                      {p.excerpt && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.excerpt}</p>}
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section data-testid="social-section" className="py-14 border-y border-line bg-white">
        <div className="container-x flex flex-col sm:flex-row items-center justify-between gap-6">
          <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight uppercase text-onyx">
            Suivez le 12e Homme
          </h2>
          <div className="flex gap-3">
            {[
              { icon: Facebook, href: settings?.social_facebook, label: "Facebook — Douzième Homme Le Jeu", id: "home-social-facebook" },
              { icon: Instagram, href: settings?.social_instagram, label: "Instagram — @douziemehomme_lejeu", id: "home-social-instagram" },
              { icon: Youtube, href: settings?.social_youtube, label: "YouTube — Douzième Homme Le Jeu", id: "home-social-youtube" },
            ].map(({ icon: Icon, href, label, id }) => (
              <a key={id} href={href || "#"} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={id}
                className="w-12 h-12 border border-line flex items-center justify-center text-onyx hover:bg-rouge hover:text-white hover:border-rouge transition-colors">
                <Icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="final-cta-section" className="py-20 sm:py-28 bg-rouge text-white">
        <div className="container-x text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight uppercase leading-[1.05]" data-testid="final-cta-title">
              {content.finalcta_title}
            </h2>
            <p className="mt-5 text-base sm:text-lg text-white/85 max-w-xl mx-auto">{content.finalcta_text}</p>
            <Link to="/acheter" data-testid="final-cta-button"
              className="mt-9 inline-block bg-white text-rouge px-10 py-4 text-sm font-bold uppercase tracking-wide hover:bg-cream hover:-translate-y-0.5 transition-all">
              Acheter le jeu
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
